import { Component, EventEmitter, Input, Output } from '@angular/core';
import { pollOptions, pollSelected, pollTotal, PollOptionView, voteLabel } from '../community-format';

/** Poll card with result bars. Voting is handled by the parent (inherited onVoteOptionClick). */
@Component({
  selector: 'app-cm-poll',
  template: `
    <div class="cm-poll" role="group" [attr.aria-label]="'Poll: ' + poll?.question">
      <div class="cm-tag">
        <mat-icon aria-hidden="true">bar_chart</mat-icon> Poll
        <span *ngIf="poll?.isAllowMultipleResponses">· Multiple choice</span>
      </div>
      <p class="cm-q">{{ poll?.question }}</p>
      <button *ngFor="let o of options" type="button" class="cm-opt" [class.pick]="isPicked(o.id)"
        [class.multi]="poll?.isAllowMultipleResponses" [attr.aria-pressed]="isPicked(o.id)" [disabled]="!canVote"
        (click)="vote.emit(o.id)">
        <span class="cm-bar" [style.width.%]="o.percent"></span>
        <span class="cm-mark" aria-hidden="true"><mat-icon *ngIf="isPicked(o.id)">check</mat-icon></span>
        <span class="cm-label">{{ o.label }}</span>
        <span class="cm-pc">{{ o.percent }}%</span>
      </button>
      <div class="cm-foot">
        <span>{{ totalLabel }}</span>
        <span>{{ footText }}</span>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; width: min(420px, 100%); }
    .cm-poll { margin-top: 8px; padding: 14px 16px; border-radius: 14px; border: 1px solid var(--cm-line);
      background: var(--cm-raise); box-shadow: 0 6px 18px -12px rgba(0, 0, 0, .45); }
    .cm-tag { display: flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; letter-spacing: .08em;
      text-transform: uppercase; color: var(--cm-accent); }
    .cm-tag mat-icon { font-size: 16px; width: 16px; height: 16px; }
    .cm-tag span { color: var(--cm-muted); font-weight: 600; letter-spacing: .04em; text-transform: none; }
    .cm-q { margin: 6px 0 10px; font-size: 15px; font-weight: 700; line-height: 1.35; word-break: break-word; }
    .cm-opt { position: relative; display: flex; align-items: center; gap: 10px; width: 100%; margin-top: 8px; padding: 10px 12px;
      border: 0; border-radius: 10px; overflow: hidden; text-align: left; cursor: pointer; font: inherit; color: inherit;
      background: var(--cm-main-bg); box-shadow: inset 0 0 0 1px var(--cm-line); transition: box-shadow .15s; }
    .cm-opt:hover:not(:disabled) { box-shadow: inset 0 0 0 1px var(--cm-accent); }
    .cm-opt:disabled { cursor: not-allowed; }
    .cm-opt:focus-visible { outline: 2px solid var(--cm-accent); outline-offset: 2px; }
    .cm-bar { position: absolute; inset: 0 auto 0 0; background: var(--cm-line); opacity: .55; transition: width .4s; }
    .cm-opt.pick { box-shadow: inset 0 0 0 1.5px var(--cm-accent); }
    .cm-opt.pick .cm-bar { background: var(--cm-tint); opacity: 1; }
    .cm-mark { position: relative; flex: none; width: 18px; height: 18px; display: grid; place-items: center;
      border-radius: 50%; box-shadow: inset 0 0 0 1.5px var(--cm-muted); }
    .cm-opt.multi .cm-mark { border-radius: 5px; }
    .cm-opt.pick .cm-mark { background: var(--cm-btn2-bg); color: var(--cm-btn2-fg); box-shadow: none; }
    .cm-mark mat-icon { font-size: 14px; width: 14px; height: 14px; }
    .cm-label { position: relative; flex: 1; min-width: 0; word-break: break-word; }
    .cm-pc { position: relative; font-weight: 700; font-variant-numeric: tabular-nums; }
    .cm-foot { display: flex; justify-content: space-between; gap: 8px; margin-top: 10px; font-size: 12px; color: var(--cm-muted); }
    @media (prefers-reduced-motion: reduce) { .cm-bar, .cm-opt { transition: none; } }
  `],
})
export class CmPollComponent {
  @Input() poll: any;
  @Input() currentUserId = '';
  @Input() canVote = true;
  @Output() vote = new EventEmitter<string>();

  get options(): PollOptionView[] {
    return pollOptions(this.poll?.options, this.poll?.votesPerOption);
  }

  get totalLabel(): string {
    return voteLabel(pollTotal(this.options));
  }

  isPicked(optionId: string): boolean {
    return pollSelected(this.poll, this.currentUserId, optionId);
  }

  get footText(): string {
    const picked = this.options.filter(o => this.isPicked(o.id)).map(o => o.label);
    if (!picked.length) return this.canVote ? 'Tap an option to vote' : 'Sign in to vote';
    return `You voted · ${picked.join(', ')}`;
  }
}
